'use client'

import * as React from 'react'

import { useInView, useMotionValue, useSpring } from 'motion/react'

import { cn } from '../../lib/utils'

interface NumberTickerProps extends React.ComponentPropsWithoutRef<'span'> {
  value: number
  startValue?: number
  direction?: 'up' | 'down'
  delay?: number
  decimalPlaces?: number
  damping?: number
  stiffness?: number
}

function NumberTicker({
  value,
  startValue = 0,
  direction = 'up',
  delay = 0,
  className,
  decimalPlaces = 0,
  damping = 30,
  stiffness = 200,
  ...props
}: NumberTickerProps) {
  const ref = React.useRef<HTMLSpanElement>(null)
  const motionValue = useMotionValue(direction === 'down' ? value : startValue)

  const springValue = useSpring(motionValue, {
    damping,
    stiffness
  })

  const isInView = useInView(ref, { once: true, margin: '0px' })

  React.useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        motionValue.set(direction === 'down' ? startValue : value)
      }, delay * 1000)

      return () => clearTimeout(timer)
    }
  }, [motionValue, isInView, delay, value, direction, startValue])

  React.useEffect(
    () =>
      springValue.on('change', latest => {
        if (ref.current) {
          ref.current.textContent = Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces
          }).format(Number(latest.toFixed(decimalPlaces)))
        }
      }),
    [springValue, decimalPlaces]
  )

  // Render the final `value` in the initial/SSR markup so crawlers and no-JS
  // paints never see the raw `startValue` (e.g. 0). The spring animation still
  // runs on the client: it starts from `startValue` and updates textContent to
  // `value` once in view.
  return (
    <span ref={ref} className={cn('inline-block tabular-nums', className)} {...props}>
      {Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      }).format(direction === 'down' ? startValue : value)}
    </span>
  )
}

export { NumberTicker, type NumberTickerProps }
